import React, { useMemo, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { useCreditPacksStore } from '@/store';
import CreditPackListItem from '@/components/Credits/CreditPackListItem';
import useTheme from '@/hooks/useNeutralTheme';
import Button from '@/components/Buttons/Button';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import BannerPlacement from '@/components/Banner/BannerPlacement';

const createStyles = (colors) => StyleSheet.create({
  sectionContainer: {
    gap: 10,
  },
  sectionHeader: {
    marginBottom: 5,
  },
  sectionSubtitle: {
    marginVertical: 5,
  },
  creditPacksSection: {
    gap: 10,
  },
  comingSoonCard: {
    borderRadius: 12,
    paddingVertical: 18,
    paddingHorizontal: 16,
    backgroundColor: colors.accent_color_primary + '15',
    borderWidth: 1,
    borderColor: colors.accent_color_primary + '35',
    alignItems: 'center',
    gap: 10,
  },
  comingSoonEmoji: {
    fontSize: 34,
    lineHeight: 38,
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 2,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.accent_color_primary + '65',
    backgroundColor: colors.accent_color_primary + '10',
  },
  ctaRow: {
    marginTop: 4,
  },
});

function CreditPacksSection({ promoCode } = {}) {
  const fetchCreditPacks = useCreditPacksStore(s => s.fetchCreditPacks);
  const isLoading = useCreditPacksStore(s => s.isLoading);
  const error = useCreditPacksStore(s => s.error);
  const creditPacks = useCreditPacksStore(s => s.creditPacks);
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  
  useEffect(() => {
    fetchCreditPacks();
  }, [fetchCreditPacks]);
  
  const comingSoon = false;
  
  if (comingSoon) {
    return (
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Text color="primary" size='xl' weight='bold'>
            Buy credit packs
          </Text>
          <Text style={styles.sectionSubtitle} size="n" color="secondary">
            Top up your credits with one-time purchases
          </Text>
        </View>
        <View style={styles.comingSoonCard}>
          <Text style={styles.comingSoonEmoji}>✨</Text>
          <Text size='l' weight='bold'>
            Credit packs are coming soon
          </Text>
          <Text size='n' color='secondary'>
            A fast, flexible way to add more credits.
          </Text>
          <View style={styles.badgesRow}>
            <View style={styles.badge}>
              <Text size='xs' weight='bold' color='primary'>One-time purchase</Text>
            </View>
            <View style={styles.badge}>
              <Text size='xs' weight='bold' color='primary'>Credits don&apos;t expire</Text>
            </View>
          </View>
        </View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Text color="primary" size='xl' weight='bold'>
            Buy credit packs
          </Text>
          <Text style={styles.sectionSubtitle} size="n" color="secondary">
            Loading credit packs...
          </Text>
        </View>
      </View>
    );
  }
  
  if (error) {
    return (
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Text color="primary" size='xl' weight='bold'>
            Buy credit packs
          </Text>
          <Text style={styles.sectionSubtitle} size="n" color="error">
            {error}
          </Text>
        </View>
      </View>
    );
  }
  
  if (!creditPacks || creditPacks.length === 0) {
    return (
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Text color="primary" size='xl' weight='bold'>
            Buy credit packs
          </Text>
          <Text style={styles.sectionSubtitle} size="n" color="secondary">
            No credit packs available at the moment.
          </Text>
        </View>
      </View>
    );
  }
  
  return (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeader}>
        <Text color="primary" size='xl' weight='bold'>
          Buy credit packs
        </Text>
        <Text style={styles.sectionSubtitle} size="n" color="secondary">
          Purchase additional credits for extended usage
        </Text>
      </View>
      
      <BannerPlacement placement="credit-store" style={{ marginBottom: 10 }} />
      <InternationalPaymentNotice style={{ marginBottom: 10 }} />

      <View style={styles.creditPacksSection}>
        {creditPacks.map((creditPack) => (
          <CreditPackListItem 
            key={`credit-pack-${creditPack.productId || creditPack.id}`}
            id={creditPack.id}
            credits={creditPack.credits || 0}
            price={creditPack.price || 0}
            name={creditPack.name}
            isBestValue={creditPack?.flags?.isBestValue === true}
            isPopular={creditPack?.flags?.isPopular === true}
            promoCode={promoCode}
          />
        ))}
      </View>
    </View>
  );
} 

export default React.memo(CreditPacksSection);